import type { Question } from "@/types";
import { eventManager } from "@/utils/event-manager";

/**
 * UI Elements interface for question display
 */
interface QuestionDisplayElements {
  questionText: HTMLElement;
  answerInput: HTMLInputElement;
  multipleChoiceOptions: HTMLElement;
  checkBtn: HTMLButtonElement;
  showAnswerBtn: HTMLButtonElement;
  feedback: HTMLElement;
}

/**
 * Question display component - handles rendering different question types
 */
export class QuestionDisplay {
  private elements: QuestionDisplayElements;

  constructor(elements: QuestionDisplayElements) {
    this.elements = elements;
  }

  /**
   * Display a question based on its type
   */
  display(question: Question): void {
    switch (question.type) {
      case "multiple-choice":
        this.displayMultipleChoice(question);
        break;
      case "translation":
        this.displayTranslation(question);
        break;
      case "fill-blank":
      default:
        this.displayFillBlank(question);
        break;
    }
  }

  /**
   * Display fill-in-the-blank question
   */
  private displayFillBlank(question: Question): void {
    const tenseName =
      (window as any).verbData?.getTenseName(question.tense) || question.tense;
    const tenseClass = this.getTenseClass(question.tense);

    const challengeIndicator = this.getChallengeIndicator();
    const adaptiveIndicator = this.getAdaptiveIndicator(
      question.isAdaptiveSelected
    );

    const questionHTML = `
      <div class="question-structure-enhanced">
        <div class="question-structure">
          <span class="tense-badge ${tenseClass}">${tenseName}</span>
          <span class="pronoun-text">${question.pronoun}</span>
          <span class="verb-blank"></span>
        </div>
        ${challengeIndicator}
      </div>
      ${adaptiveIndicator}
    `;

    this.elements.questionText.innerHTML = questionHTML;

    // Setup UI state for fill-blank
    this.elements.answerInput.style.display = "block";
    this.elements.multipleChoiceOptions.style.display = "none";
    this.elements.answerInput.value = "";
    this.elements.answerInput.disabled = false;
    this.elements.answerInput.focus();
    this.elements.checkBtn.disabled = false;
    this.elements.checkBtn.style.display = "inline-block";
    this.elements.showAnswerBtn.disabled = false;
    this.elements.feedback.style.display = "none";
  }

  /**
   * Display multiple choice question
   */
  private displayMultipleChoice(question: Question): void {
    const tenseName =
      (window as any).verbData?.getTenseName(question.tense) || question.tense;
    const tenseClass = this.getTenseClass(question.tense);

    const challengeIndicator = this.getChallengeIndicator();
    const adaptiveIndicator = this.getAdaptiveIndicator(
      question.isAdaptiveSelected
    );

    const questionHTML = `
      <div class="question-structure-enhanced">
        <div class="question-structure">
          <span class="tense-badge ${tenseClass}">${tenseName}</span>
          <span class="pronoun-text">${question.pronoun}</span>
          <span class="verb-blank"></span>
        </div>
        ${challengeIndicator}
      </div>
      ${adaptiveIndicator}
    `;

    this.elements.questionText.innerHTML = questionHTML;

    // Setup UI state for multiple choice
    this.elements.answerInput.style.display = "none";
    this.elements.multipleChoiceOptions.style.display = "block";
    this.elements.checkBtn.disabled = true;
    this.elements.checkBtn.style.display = "none";
    this.elements.showAnswerBtn.disabled = false;
    this.elements.feedback.style.display = "none";

    // Create option buttons
    const options = question.options || [];
    this.elements.multipleChoiceOptions.innerHTML = options
      .map(
        (option) =>
          `<button class="choice-option" data-answer="${option}">${option}</button>`
      )
      .join("");

    // Add click handlers for immediate feedback
    this.setupMultipleChoiceHandlers(question);
  }

  /**
   * Display translation question
   */
  private displayTranslation(question: Question): void {
    const tenseName =
      (window as any).verbData?.getTenseName(question.tense) || question.tense;
    const tenseClass = this.getTenseClass(question.tense);

    const challengeIndicator = this.getChallengeIndicator();
    const adaptiveIndicator = this.getAdaptiveIndicator(
      question.isAdaptiveSelected
    );

    const questionHTML = `
      <div class="translation-question-enhanced">
        <div class="translation-tense-badge">
          <span class="tense-badge ${tenseClass}">Translate to ${tenseName}</span>
          ${challengeIndicator}
          ${adaptiveIndicator}
        </div>
        <div class="english-sentence-enhanced">"${
          question.englishSentence || ""
        }"</div>
      </div>
    `;

    this.elements.questionText.innerHTML = questionHTML;

    // Setup UI state for translation
    this.elements.answerInput.style.display = "block";
    this.elements.multipleChoiceOptions.style.display = "none";
    this.elements.answerInput.value = "";
    this.elements.answerInput.disabled = false;
    this.elements.answerInput.focus();
    this.elements.checkBtn.disabled = false;
    this.elements.checkBtn.style.display = "inline-block";
    this.elements.showAnswerBtn.disabled = false;
    this.elements.feedback.style.display = "none";
  }

  /**
   * Setup multiple choice click handlers with immediate feedback
   */
  private setupMultipleChoiceHandlers(question: Question): void {
    const buttons =
      this.elements.multipleChoiceOptions.querySelectorAll(".choice-option");

    buttons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const target = e.target as HTMLButtonElement;
        if (target.disabled) return;

        // Disable all buttons
        buttons.forEach((b) => {
          (b as HTMLButtonElement).disabled = true;
          b.classList.remove("selected");
        });

        // Mark as selected
        target.classList.add("selected");

        // Get correct answer and provide visual feedback
        const correctAnswer = question.answer;
        const selectedAnswer = target.dataset.answer || "";
        const isCorrect = selectedAnswer === correctAnswer;

        // Add visual feedback
        if (isCorrect) {
          target.classList.add("correct");
        } else {
          target.classList.add("incorrect");
          // Highlight correct answer
          buttons.forEach((b) => {
            if ((b as HTMLElement).dataset.answer === correctAnswer) {
              b.classList.add("correct");
            }
          });
        }

        // Show immediate feedback message to user
        const feedbackElement = this.elements.feedback;
        if (feedbackElement) {
          feedbackElement.innerHTML = `
            <div style="text-align: center; padding: 10px; margin: 10px 0; border-radius: 8px; background: #f8f9fa; border: 1px solid #dee2e6;">
              <span style="color: #6c757d;">Answer registered! Click "Continue to Next Question" below...</span>
            </div>
          `;
          feedbackElement.style.display = "block";
        }

        // Emit check answer event after delay
        setTimeout(() => {
          eventManager.emit("ui:check-answer", undefined);
        }, 200);
      });
    });
  }

  /**
   * Get challenge indicator HTML
   */
  private getChallengeIndicator(): string {
    const app = (window as any).app;
    if (!app || !app.isSmartModeEnabled() || !app.currentChallengeLevel) {
      return "";
    }

    const challengeLevel = app.currentChallengeLevel;
    const challengeName = this.getChallengeName(challengeLevel);

    return `
      <div class="challenge-type-badge challenge-type-${challengeLevel}">
        <span>🎯 ${challengeName}</span>
        <div class="challenge-progress-stars">
          ${Array.from(
            { length: 3 },
            (_, i) =>
              `<div class="challenge-star ${
                i < challengeLevel ? "filled" : ""
              }"></div>`
          ).join("")}
        </div>
      </div>
    `;
  }

  /**
   * Get adaptive learning indicator HTML
   */
  private getAdaptiveIndicator(isAdaptiveSelected?: boolean): string {
    if (!isAdaptiveSelected) return "";

    return `<span class="adaptive-indicator" title="Smart Learning: This question was prioritized based on your progress">🎯</span>`;
  }

  /**
   * Get CSS class for tense styling
   */
  private getTenseClass(tense: string): string {
    const tenseMap: Record<string, string> = {
      present: "present",
      past: "past",
      perfect: "past",
      pluperfect: "past",
      subjunctive: "subjunctive",
      subjunctiveII: "subjunctive",
      future: "future",
      futureII: "future",
    };

    return tenseMap[tense] || "mixed";
  }

  /**
   * Get challenge name for display
   */
  private getChallengeName(level: number): string {
    const names: Record<number, string> = {
      1: "Multiple Choice",
      2: "Fill the Blank",
      3: "Translation",
    };
    return names[level] || "Unknown";
  }
}

// Export for global usage
export default QuestionDisplay;

// Make available globally for backward compatibility
declare global {
  interface Window {
    QuestionDisplay: any;
  }
}

if (typeof window !== "undefined") {
  window.QuestionDisplay = QuestionDisplay;
}
